/**
 * Today's plan: focus blocks interleaved with Outlook meetings, in time
 * order. A block that isn't over yet offers "Start on this block" while the
 * timer is free; the running block and the next one are marked.
 */
import { clsx } from "clsx";

import type { FocusBlock, OutlookEvent } from "../../api/focus";
import { blockTiming, isBusyEvent } from "../../lib/focus";
import { formatHm, formatMinutes } from "../../lib/focusTime";

type AgendaItem =
  | { kind: "block"; start: number; end: number; block: FocusBlock }
  | { kind: "event"; start: number; end: number; event: OutlookEvent };

export function TodayAgenda({
  blocks,
  events,
  nowMs,
  canStart,
  plannableGoalIds,
  activeBlockId,
  onStartBlock,
  onOpenBlock,
}: {
  blocks: FocusBlock[];
  events: OutlookEvent[];
  nowMs: number;
  /** The timer is free for a new focus (idle or on a break). */
  canStart: boolean;
  plannableGoalIds: Set<string>;
  /** The block the running focus belongs to. */
  activeBlockId: string | null;
  onStartBlock: (block: FocusBlock) => void;
  onOpenBlock: (block: FocusBlock) => void;
}) {
  const allDay = events.filter((e) => e.all_day);
  const items: AgendaItem[] = [
    ...blocks.map((block) => ({
      kind: "block" as const,
      start: Date.parse(block.starts_at),
      end: Date.parse(block.ends_at),
      block,
    })),
    ...events
      .filter((e) => !e.all_day && e.starts_at && e.ends_at)
      .map((event) => ({
        kind: "event" as const,
        start: Date.parse(event.starts_at as string),
        end: Date.parse(event.ends_at as string),
        event,
      })),
  ].sort((a, b) => a.start - b.start || a.end - b.end);
  const nextBlock = items.find(
    (i): i is Extract<AgendaItem, { kind: "block" }> => i.kind === "block" && i.start > nowMs,
  );
  const nextId = nextBlock?.block.id ?? null;

  if (items.length === 0 && allDay.length === 0) {
    return (
      <div className="empty-state">
        <div className="title">Nothing planned today</div>
        <p className="hint">Draw focus blocks in the Week view – or just start a focus.</p>
      </div>
    );
  }

  return (
    <ul className="agenda">
      {allDay.map((e) => (
        <li key={e.id} className="agenda-row is-event">
          <span className="agenda-time">all day</span>
          <span className="agenda-title">{e.subject || "(no subject)"}</span>
        </li>
      ))}
      {items.map((item) => {
        const past = item.end <= nowMs;
        const now = item.start <= nowMs && nowMs < item.end;
        const time = (
          <span className="agenda-time">
            {formatHm(item.start)}–{formatHm(item.end)}
          </span>
        );
        if (item.kind === "event") {
          const e = item.event;
          return (
            <li
              key={`e:${e.id}`}
              className={clsx("agenda-row is-event", past && "is-past", now && "is-now")}
            >
              {time}
              <span className="agenda-title">{e.subject || "(no subject)"}</span>
              <span className="agenda-meta">{isBusyEvent(e) ? "meeting" : (e.show_as ?? "event")}</span>
            </li>
          );
        }
        const b = item.block;
        const timing = blockTiming(b, nowMs);
        const startable =
          canStart && timing !== "past" && b.status === "planned" && plannableGoalIds.has(b.goal_id);
        return (
          <li
            key={`b:${b.id}`}
            className={clsx(
              "agenda-row is-block",
              b.protection === "protected" && "is-protected",
              past && "is-past",
              now && "is-now",
              b.status !== "planned" && "is-removed",
            )}
          >
            {time}
            <button type="button" className="agenda-title agenda-link" onClick={() => onOpenBlock(b)}>
              {b.goal_title ?? "Untitled goal"}
              {b.status === "removed_externally" ? " · removed in Outlook" : ""}
            </button>
            <span className="agenda-meta">
              {activeBlockId === b.id
                ? "focusing"
                : now
                  ? "now"
                  : b.id === nextId
                    ? "next"
                    : b.actual_minutes > 0
                      ? `${formatMinutes(b.actual_minutes)} done`
                      : b.protection}
            </span>
            {startable ? (
              <button type="button" className="btn btn-sm btn-second" onClick={() => onStartBlock(b)}>
                Start on this block
              </button>
            ) : null}
          </li>
        );
      })}
    </ul>
  );
}
